import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, forkJoin, of, throwError, timer } from 'rxjs';
import { map, catchError, retry, retryWhen, delay, take, tap } from 'rxjs/operators';
import { AppConfig } from '../app.config';
import { Media } from '../models/Media';

export interface ChunkInfo {
  index: number;
  start: number;
  end: number;
  size: number;
  data: Blob;
  uploaded: boolean;
  failed: boolean;
  checksum?: string;
}

export interface UploadProgress {
  uploadId: string;
  fileName: string;
  totalSize: number;
  uploadedSize: number;
  progressPercentage: number;
  speed: number; // bytes per second
  estimatedTimeRemaining: number; // seconds
  status: 'PREPARING' | 'UPLOADING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  chunks: ChunkInfo[];
  error?: string;
  mediaId?: string;
  chunkPath?: string;
}

export interface UploadOptions {
  chunks?: ChunkInfo[];
  mediaData?: Partial<Media>;
  onProgress?: (progress: UploadProgress) => void;
  onComplete?: (result: any) => void;
  onError?: (error: any) => void;
  /** Relative to the uploadmedia API prefix. Default: `/uploadmedia/chunk`. */
  chunkPath?: string;
}

export interface ChunkResponse {
  uploadId: string;
  status: string;
  receivedChunks: number;
  totalChunks: number;
  totalSize: number;
  uploadedSize: number;
  progressPercentage: number;
  createdAt: string;
  expiresAt: string;
  originalFilename: string;
  contentType: string;
  mediaId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ChunkedUploadService {
  private readonly CHUNK_SIZE = 5 * 1024 * 1024; // 5MB
  private readonly MAX_CONCURRENT_CHUNKS = 1;
  private readonly MAX_RETRIES = 1;
  private readonly RETRY_DELAY = 1000; // 1 second
  private readonly BACKOFF_MULTIPLIER = 2;
  private readonly MAX_FILE_SIZE_TRADITIONAL = 50 * 1024 * 1024; // 50MB

  private activeUploads = new Map<string, UploadProgress>();
  private uploadProgressSubject = new BehaviorSubject<Map<string, UploadProgress>>(new Map());
  private cancelledUploads = new Set<string>(); // Para rastrear uploads cancelados

  // Endpoints do backend
  private readonly apiVersion = `v${AppConfig.data['apiVersion']}`;
  private readonly uploadMediaEndpoint = `${AppConfig.data['services']['uploadMedia']}/${this.apiVersion}`;

  constructor(private http: HttpClient) { }

  /**
   * Valida se o arquivo deve usar upload chunked
   */
  validateFileSize(file: File): boolean {
    return file.size >= this.MAX_FILE_SIZE_TRADITIONAL;
  }

  /**
   * Prepara chunks de um arquivo sem carregar o arquivo completo na memória
   */
  prepareChunks(file: File): ChunkInfo[] {
    const chunks: ChunkInfo[] = [];
    const totalChunks = Math.ceil(file.size / this.CHUNK_SIZE);

    for (let i = 0; i < totalChunks; i++) {
      const start = i * this.CHUNK_SIZE;
      const end = Math.min(start + this.CHUNK_SIZE, file.size);
      const size = end - start;

      chunks.push({
        index: i + 1,
        start,
        end,
        size,
        data: file.slice(start, end),
        uploaded: false,
        failed: false
      });
    }

    return chunks;
  }

  /**
   * Upload de um arquivo usando chunks
   */
  uploadFile(file: File, options: UploadOptions = {}): Observable<UploadProgress> {
    const uploadId = this.generateUploadId();
    const chunks = options.chunks || this.prepareChunks(file);

    const chunkPath = options.chunkPath || '/uploadmedia/chunk';
    const progress: UploadProgress = {
      uploadId,
      fileName: file.name,
      totalSize: file.size,
      uploadedSize: 0,
      progressPercentage: 0,
      speed: 0,
      estimatedTimeRemaining: 0,
      status: 'PREPARING',
      chunks,
      chunkPath
    };

    this.activeUploads.set(uploadId, progress);
    this.updateProgress();

    return new Observable(observer => {
      progress.status = 'UPLOADING';
      this.updateProgress();

      // Upload chunks sequencialmente (um após o outro)
      this.uploadChunksSequentially(file, chunks, uploadId, progress, observer, options);
    });
  }

  /**
   * Upload de múltiplos arquivos
   */
  uploadMultipleFiles(files: File[], options: UploadOptions = {}): Observable<UploadProgress[]> {
    const uploads = files.map(file => this.uploadFile(file, options));
    return forkJoin(uploads);
  }

  /**
   * Cancela um upload em andamento
   */
  cancelUpload(uploadId: string): Observable<any> {
    const progress = this.activeUploads.get(uploadId);
    if (progress) {
      // Marcar como cancelado para interromper o processo
      this.cancelledUploads.add(uploadId);
      progress.status = 'CANCELLED';
      
      // Chamar endpoint do backend para cancelar upload
      return this.http.delete(`${this.uploadMediaEndpoint}${progress.chunkPath || '/uploadmedia/chunk'}/${uploadId}`)
        .pipe(
          catchError(error => {
            console.warn('Failed to cancel upload on backend:', error);
            // Mesmo se falhar no backend, continuamos com o cancelamento local
            return of(null);
          }),
          tap(() => {
            // Limpar upload local após chamada do backend
            this.activeUploads.delete(uploadId);
            this.cancelledUploads.delete(uploadId);
            this.updateProgress();
          })
        );
    }
    
    return of(null);
  }

  /**
   * Retry de chunks falhados
   */
  retryFailedChunks(uploadId: string): Observable<UploadProgress> {
    const progress = this.activeUploads.get(uploadId);
    if (!progress) {
      return throwError(() => new Error('Upload not found'));
    }

    const failedChunks = progress.chunks.filter(chunk => chunk.failed);
    if (failedChunks.length === 0) {
      return of(progress);
    }

    progress.status = 'UPLOADING';
    this.updateProgress();

    return new Observable(observer => {
      this.uploadChunksSequentially(
        null, // file não é necessário para retry
        failedChunks,
        uploadId,
        progress,
        observer,
        { chunkPath: progress.chunkPath }
      );
    });
  }

  /**
   * Upload de chunks sequencialmente (um após o outro)
   */
  private uploadChunksSequentially(
    file: File | null,
    chunks: ChunkInfo[],
    uploadId: string,
    progress: UploadProgress,
    observer: any,
    options: UploadOptions
  ): void {
    const startTime = Date.now();
    let currentChunkIndex = 0;

    const uploadNextChunk = async (): Promise<void> => {
      // Verificar se o upload foi cancelado
      if (this.cancelledUploads.has(uploadId)) {
        progress.status = 'CANCELLED';
        this.activeUploads.delete(uploadId);
        this.cancelledUploads.delete(uploadId);
        this.updateProgress();
        observer.error(new Error('Upload cancelled by user'));
        return;
      }

      if (currentChunkIndex >= chunks.length) {
        // Todos os chunks foram enviados
        progress.status = 'COMPLETED';
        this.activeUploads.delete(uploadId);
        this.updateProgress();
        observer.next(progress);
        observer.complete();
        options.onComplete?.(progress);
        return;
      }

      const chunk = chunks[currentChunkIndex];
      
      try {
        const response = await this.uploadChunk(
          chunk,
          uploadId,
          file?.name || '',
          file?.type || '',
          file,
          options.mediaData,
          options.chunkPath || progress.chunkPath || '/uploadmedia/chunk'
        );
        
        if (response.mediaId) {
          progress.mediaId = response.mediaId;
        }
        
        chunk.uploaded = true;
        chunk.failed = false;
        currentChunkIndex++;

        // Atualizar progresso
        progress.uploadedSize += chunk.size;
        progress.progressPercentage = (progress.uploadedSize / progress.totalSize) * 100;

        // Calcular velocidade e tempo restante
        const elapsed = (Date.now() - startTime) / 1000;
        progress.speed = progress.uploadedSize / elapsed;
        const remainingSize = progress.totalSize - progress.uploadedSize;
        progress.estimatedTimeRemaining = remainingSize / progress.speed;

        this.updateProgress();
        options.onProgress?.(progress);

        // Verificar novamente se foi cancelado antes de continuar
        if (this.cancelledUploads.has(uploadId)) {
          progress.status = 'CANCELLED';
          this.activeUploads.delete(uploadId);
          this.cancelledUploads.delete(uploadId);
          this.updateProgress();
          observer.error(new Error('Upload cancelled by user'));
          return;
        }

        // Continuar com o próximo chunk
        await uploadNextChunk();
        
      } catch (error: any) {
        chunk.failed = true;
        progress.error = error.message;
        this.updateProgress();
        options.onError?.(error);

        // Marcar upload como falhado
        progress.status = 'FAILED';
        this.activeUploads.delete(uploadId);
        this.updateProgress();
        observer.error(error);
      }
    };

    // Iniciar upload do primeiro chunk
    uploadNextChunk();
  }

  /**
   * Upload de um chunk individual
   */
  private uploadChunk(
    chunk: ChunkInfo,
    uploadId: string,
    fileName: string,
    contentType: string,
    file: File,
    mediaData?: Partial<Media>,
    chunkPath = '/uploadmedia/chunk'
  ): Promise<ChunkResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        // Verificar se o upload foi cancelado antes de processar o chunk
        if (this.cancelledUploads.has(uploadId)) {
          reject(new Error('Upload cancelled by user'));
          return;
        }

        // Converter chunk para base64
        const reader = new FileReader();
        reader.onload = async () => {
          try {
            const base64Data = (reader.result as string).split(',')[1];

            // Calcular checksum MD5 do base64 (igual ao backend)
            const checksum = await this.calculateChecksum(base64Data);

            const chunkData = {
              uploadId,
              chunkNumber: chunk.index,
              totalChunks: Math.ceil(file.size / this.CHUNK_SIZE),
              chunkData: base64Data,
              checksum: checksum,
              originalFilename: fileName,
              contentType: mediaData?.content_type || contentType,
              totalSize: file.size,
              // Dados do Media
              title: mediaData?.title || '',
              description: mediaData?.description || '',
              publishingDate: mediaData?.publishing_date || '',
              publishingHour: mediaData?.publishing_hour || '',
              expirationDate: mediaData?.expiration_date || '',
              expirationHour: mediaData?.expiration_hour || '',
              tags: mediaData?.tags ? (Array.isArray(mediaData.tags) ? mediaData.tags.join(',') : mediaData.tags) : '',
              mimeCodec: mediaData?.mime_codec || '',
              largerSize: mediaData?.larger_size || false,
              maxSize: mediaData?.max_size || 0
            };

            this.http.post<ChunkResponse>(`${this.uploadMediaEndpoint}${chunkPath}`, chunkData)
              .pipe(
                retry({
                  count: this.MAX_RETRIES,
                  delay: (error, retryCount) => {
                    const delay = this.RETRY_DELAY * Math.pow(this.BACKOFF_MULTIPLIER, retryCount - 1);
                    return timer(delay);
                  }
                }),
                catchError(error => {
                  console.error('Chunk upload failed:', error);
                  return throwError(() => error);
                })
              )
              .subscribe({
                next: (response) => resolve(response),
                error: (error) => reject(error)
              });
          } catch (error) {
            reject(error);
          }
        };

        reader.onerror = () => reject(new Error('Failed to read chunk data'));
        reader.readAsDataURL(chunk.data);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Calcula checksum SHA-256 do base64 para validação (igual ao backend)
   */
  private async calculateChecksum(base64Data: string): Promise<string> {
    try {
      // Decodifica Base64 em bytes crus
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Hash SHA-256 sobre os bytes
      const hashBuffer = await crypto.subtle.digest('SHA-256', bytes);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      // Sempre 64 caracteres hex (256 bits)
      return hashHex;
    } catch (error) {
      console.error('Error calculating checksum:', error);
      return '';
    }
  }


  /**
   * Gera ID único para upload
   */
  private generateUploadId(): string {
    return 'upload_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Atualiza o progresso de todos os uploads
   */
  private updateProgress(): void {
    this.uploadProgressSubject.next(new Map(this.activeUploads));
  }

  /**
   * Observable para acompanhar progresso de uploads
   */
  getUploadProgress(): Observable<Map<string, UploadProgress>> {
    return this.uploadProgressSubject.asObservable();
  }

  /**
   * Obtém progresso de um upload específico
   */
  getUploadProgressById(uploadId: string): UploadProgress | undefined {
    return this.activeUploads.get(uploadId);
  }

  /**
   * Limpa uploads completados ou falhados
   */
  cleanupCompletedUploads(): void {
    for (const [uploadId, progress] of this.activeUploads.entries()) {
      if (progress.status === 'COMPLETED' || progress.status === 'FAILED' || progress.status === 'CANCELLED') {
        this.activeUploads.delete(uploadId);
        this.cancelledUploads.delete(uploadId);
      }
    }
    this.updateProgress();
  }
}
